<template>
    <AcademyLayout 
    :custom-banner="selectedCourseBanner"
    :canEditBanner="true"
    @banner-updated="handleBannerUpdate">
        <main class="container-fluid" v-if="institutionData">
            <div class="courses-management-container">
                <div class="header-section">
                    <h2>{{ $t('academy.course-creation') }}</h2>
                    <b-button variant="primary" @click="showCreateModal = true">
                        <b-icon-plus-circle class="mr-2"></b-icon-plus-circle>
                        {{ $t('academy.create-new-course') }}
                    </b-button>
                </div>

                <!-- Filtros -->
                <div class="filters-section mb-4">
                    <b-row>
                        <b-col md="6">
                            <b-form-group :label="$t('academy.search-by-name')">
                                <b-form-input
                                    v-model="searchQuery"
                                    :placeholder="$t('academy.search-course-placeholder')"
                                    @input="filterCourses"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <!-- <b-col md="3">
                            <b-form-group label="Filtrar por estado">
                                <b-form-select
                                    v-model="selectedStatus"
                                    :options="statusOptions"
                                    @change="filterCourses"
                                ></b-form-select>
                            </b-form-group>
                        </b-col> -->
                        <!-- <b-col md="3">
                            <b-form-group label="Filtrar por modalidad">
                                <b-form-select
                                    v-model="selectedModality"
                                    :options="modalityOptions"
                                    @change="filterCourses"
                                ></b-form-select>
                            </b-form-group>
                        </b-col> -->
                    </b-row>
                </div>

                <!-- Tabla de Cursos -->
                <div class="table-responsive">
                    <b-table
                        :items="filteredCourses"
                        :fields="fields"
                        striped
                        hover
                        responsive
                        class="courses-table"
                    >
                        <template #cell(image)="data">
                            <div class="course-image-container">
                                <img 
                                    :src="data.item.image || noImage" 
                                    :alt="data.item.name"
                                    @error="handleImageError($event, data.item)"
                                    class="course-image"
                                />
                                <!-- Overlay con botón de edición -->
                                <div class="image-edit-overlay">
                                    <b-button
                                        variant="light"
                                        size="sm"
                                        class="edit-image-btn"
                                        @click="openImageEditor(data.item)"
                                        v-b-tooltip.hover
                                        :title="$t('academy.edit_cover')"
                                    >
                                        <b-icon-pencil></b-icon-pencil>
                                    </b-button>
                                </div>
                            </div>
                        </template>

                        <template #cell(name)="data">
                            <div class="d-flex flex-column">
                                <strong>{{ data.item.name }}</strong>
                                <small class="text-muted">{{ truncateText(data.item.description, 60) }}</small>
                            </div>
                        </template>

                        <template #cell(institution)="data">
                            <b-badge variant="primary">{{ data.item.institution }}</b-badge>
                        </template>

                        <template #cell(modules)="data">
                            <b-badge variant="info">{{ $tc('academy.number_lessons', data.item.modules?.length || 0) }}</b-badge>
                        </template>

                        <template #cell(isRemote)="data">
                            <b-badge :variant="data.item.isRemote ? 'success' : 'warning'">
                                {{ data.item.isRemote ? $t('academy.remote') : $t('academy.in-person') }}
                            </b-badge>
                        </template>

                        <template #cell(block)="data">
                            <b-badge :variant="data.item.block ? 'danger' : 'success'">
                                {{ data.item.block ? $t('academy.locked') : $t('academy.active') }}
                            </b-badge>
                        </template>

                        <template #cell(status)="data">
                            <b-badge :variant="data.item.isPublished ? 'primary' : 'secondary'">
                                <b-icon-eye v-if="data.item.isPublished" class="mr-1"></b-icon-eye>
                                <b-icon-eye-slash v-else class="mr-1"></b-icon-eye-slash>
                                {{ data.item.isPublished ? $t('academy.published') : $t('academy.draft') }}
                            </b-badge>
                        </template>

                        <template #cell(actions)="data">
                            <div class="action-buttons">
                                <b-button
                                    variant="outline-primary"
                                    size="sm"
                                    @click="navigateEditCourse(data.item)"
                                    class="mr-2"
                                    v-b-tooltip.hover
                                    :title="$t('academy.edit-course')"
                                >
                                    <b-icon-pencil></b-icon-pencil>
                                </b-button>
                                <b-button
                                    variant="outline-info"
                                    size="sm"
                                    @click="manageModules(data.item)"
                                    class="mr-2"
                                    v-b-tooltip.hover
                                    :title="$t('academy.manage_lessons')"
                                >
                                    <b-icon-collection></b-icon-collection>
                                </b-button>
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    @click="confirmDelete(data.item)"
                                    v-b-tooltip.hover
                                    :title="$t('academy.delete-course')"
                                >
                                    <b-icon-trash></b-icon-trash>
                                </b-button>
                            </div>
                        </template>
                    </b-table>
                </div>

                <!-- Modal para Crear/Editar Curso -->
                <b-modal
                    v-model="showCreateModal"
                    :title="isEditing ? $t('academy.edit-course') : $t('academy.create-new-course')"
                    @ok="handleSaveCourse"
                    @hidden="resetForm"
                    size="xl"
                    :ok-title="$t('academy.save')"
                    :cancel-title="$t('academy.cancel')"
                >
                    <b-form @submit.stop.prevent="handleSaveCourse">
                        <b-row>
                            <b-col md="8">
                                <b-form-group :label="$t('academy.course-name-label')+ ' *'">
                                    <b-form-input
                                        v-model="courseForm.name"
                                        required
                                        :placeholder="$t('academy.course-name-placeholder')"
                                    ></b-form-input>
                                </b-form-group>

                                <b-form-group :label="$t('academy.description') +' *'">
                                    <b-form-textarea
                                        v-model="courseForm.description"
                                        required
                                        rows="3"
                                        :placeholder="$t('academy.course-description-placeholder')"
                                    ></b-form-textarea>
                                </b-form-group>

                                <b-form-group :label="$t('academy.institution')">
                                    <b-form-input
                                        :value="institutionData ? institutionData.name : ''"
                                        disabled
                                        readonly
                                        class="bg-light"
                                    ></b-form-input>
                                </b-form-group>

                                <b-form-group :label="$t('academy.modality')">
                                <div class="modality-options d-flex align-items-center mb-3">
                                    <b-button
                                    :variant="courseForm.modality === 'free' ? 'modality-active' : 'modality-inactive'"
                                    @click="courseForm.modality = 'free'"
                                    class="modality-btn mr-3"
                                    >
                                    Free
                                    </b-button>
                                    <b-button
                                    :variant="courseForm.modality === 'premium' ? 'modality-active-premium' : 'modality-inactive'"
                                    @click="courseForm.modality = 'premium'"
                                    class="modality-btn"
                                    >
                                    Premium
                                    </b-button>
                                </div>
                                </b-form-group>

                                <b-row>
                                    <!-- <b-col md="6">
                                        <b-form-group>
                                            <label>Modalidad</label>
                                            <b-form-checkbox v-model="courseForm.isRemote">
                                                Curso remoto
                                            </b-form-checkbox>
                                        </b-form-group>
                                    </b-col>
                                    <b-col md="6">
                                        <b-form-group>
                                            <label>Estado</label>
                                            <b-form-checkbox v-model="courseForm.block">
                                                Bloquear curso
                                            </b-form-checkbox>
                                        </b-form-group>
                                    </b-col> -->
                                </b-row>
                            </b-col>
                            <b-col md="4">
                                <b-form-group :label="$t('academy.cover_image')">
                                    <div class="image-upload-section">
                                        <div class="current-image" v-if="courseForm.image">
                                            <img :src="courseForm.image" :alt="$t('academy.current-banner')" />
                                        </div>
                                        <b-form-file
                                            v-model="selectedFile"
                                            :placeholder="$t('academy.select-image') + '...'"
                                            accept="image/*"
                                            @change="handleFileUpload"
                                        ></b-form-file>
                                        <small class="text-muted">{{ $t('academy.image-formats') }}</small>
                                    </div>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <hr>

                        <!-- Configuración de Lecciones/Lecciones -->
                        <div class="modules-configuration">
                            <h5>{{ $t('academy.lessons_settings') }}</h5>
                            
                            <div class="add-module-section mb-3">
                                <b-button 
                                    variant="outline-success" 
                                    size="sm" 
                                    @click="addModule"
                                >
                                    <b-icon-plus></b-icon-plus> {{ $t('academy.add_lesson') }}
                                </b-button>
                            </div>

                            <div class="modules-list">
                                <div 
                                    v-for="(module, moduleIndex) in courseForm.modules" 
                                    :key="moduleIndex"
                                    class="module-item"
                                >
                                    <div class="module-header">
                                        <b-row>
                                            <b-col md="6">
                                                <b-form-group :label="`${$t('academy.module')} ${module.number} - ${$t('academy.name')} `">
                                                    <b-form-input
                                                        v-model="module.alias"
                                                        :placeholder="$t('academy.module-name')"
                                                    ></b-form-input>
                                                </b-form-group>
                                            </b-col>
                                            <b-col md="1" class="d-flex align-items-end">
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    @click="removeModule(moduleIndex)"
                                                >
                                                    <b-icon-trash></b-icon-trash>
                                                </b-button>
                                            </b-col>
                                        </b-row>
                                    </div>

                                    <b-form-group :label="$t('academy.module-description')">
                                        <b-form-textarea
                                            v-model="module.description"
                                            rows="2"
                                            :placeholder="$t('academy.module-description')"
                                        ></b-form-textarea>
                                    </b-form-group>

                                    <div class="activities-section">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{{ $t('academy.activities') }}</strong>
                                            <b-button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                @click="addActivity(moduleIndex)"
                                            >
                                                <b-icon-plus></b-icon-plus> {{ $t('academy.add-activity') }}
                                            </b-button>
                                        </div>

                                        <div 
                                            v-for="(activity, activityIndex) in module.lessons[0]?.classActivities || []" 
                                            :key="activityIndex"
                                            class="activity-item-expanded"
                                        >
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.activity-name')+ ' *'">
                                                        <b-form-input
                                                            v-model="activity.name"
                                                            :placeholder="$t('academy.activity-name')"
                                                            required
                                                        ></b-form-input>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.initial-description') + ' *'">
                                                        <b-form-textarea
                                                            v-model="activity.InitialProjectDescription"
                                                            :placeholder="$t('academy.initial-description')"
                                                            rows="3"
                                                            required
                                                        ></b-form-textarea>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            
                                            <b-row>
                                                <b-col md="12">
                                                    <b-form-group :label="$t('academy.activity-intro')+ ' *'">
                                                        <b-form-textarea
                                                            v-model="activity.introductionText"
                                                            :placeholder="$t('academy.activity-intro-placeholder')"
                                                            rows="4"
                                                            required
                                                        ></b-form-textarea>
                                                    </b-form-group>
                                                </b-col>
                                            </b-row>
                                            
                                            <div class="d-flex justify-content-end">
                                                <b-button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    @click="removeActivity(moduleIndex, activityIndex)"
                                                >
                                                    <b-icon-trash></b-icon-trash> {{ $t('academy.delete') }}
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-form>
                </b-modal>

                <!-- Modal para Gestionar Lecciones -->
                <b-modal
                    v-model="showModulesModal"
                    :title="$t('academy.manage_course_lessons')"
                    size="xl"
                    hide-footer
                >
                    <div class="modules-management" v-if="currentCourse">
                        <div class="course-info mb-4">
                            <h5>{{ currentCourse.name }}</h5>
                            <p class="text-muted">{{ currentCourse.description }}</p>
                        </div>

                        <div class="modules-detailed-list">
                            <div 
                                v-for="(module, index) in currentCourse.modules" 
                                :key="index"
                                class="module-detail-card"
                            >
                                <b-card>
                                    <div class="module-detail-header">
                                        <h6>{{ $t('academy.lesson') }} {{ module.number }}: {{ module.name }}</h6>
                                        <div class="module-actions">
                                            <b-button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                @click="editModule(index)"
                                            >
                                                <b-icon-pencil></b-icon-pencil>
                                            </b-button>
                                            <b-button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                @click="confirmDeleteModule(index)"
                                            >
                                                <b-icon-trash></b-icon-trash>
                                            </b-button>
                                        </div>
                                    </div>
                                    
                                    <p><strong>{{$t('academy.description')}}:</strong> {{ module.description }}</p>
                                    
                                    <div class="activities-summary">
                                        <strong>{{$t('academy.activities')}} ({{ module.lessons[0]?.classActivities?.length || 0 }}):</strong>
                                        <ul v-if="module.lessons[0]?.classActivities?.length">
                                            <li v-for="activity in module.lessons[0].classActivities" :key="activity.id">
                                                {{ activity.name }}
                                            </li>
                                        </ul>
                                        <p v-else class="text-muted">{{ $t('academy.no-activities') }}</p>
                                    </div>
                                </b-card>
                            </div>
                        </div>
                    </div>
                </b-modal>
                <b-modal
                    v-model="showImageEditorModal"
                    :title="$t('academy.edit_image')"
                    size="md"
                    @ok="saveImageChanges"
                    @hidden="resetImageEditor"
                    :ok-disabled="isUploadingImage"
                    :ok-title="$t('academy.save')"
                    :cancel-title="$t('academy.cancel')"
                >
                    <div v-if="currentImageCourse">
                        <!-- Información del curso -->
                        <div class="course-info-header mb-3">
                            <h6><strong>{{ currentImageCourse.name }}</strong></h6>
                            <small class="text-muted">{{ currentImageCourse.description }}</small>
                        </div>

                        <!-- Vista previa de la imagen actual -->
                        <div class="current-image-preview mb-4">
                            <h6>{{ $t('academy.current_image') }}:</h6>
                            <div class="image-preview-container">
                                <img 
                                    :src="currentImageCourse.image || noImage" 
                                    :alt="$t('academy.current_image')"
                                    class="current-preview-image"
                                />
                            </div>
                        </div>

                        <!-- Selector de nueva imagen -->
                        <b-form-group :label="$t('academy.select_new_image')">
                            <b-form-file
                                v-model="newImageFile"
                                accept="image/*"
                                :placeholder="$t('academy.select-image') + '...'"
                                @change="handleNewImageChange"
                            />
                            <small class="form-text text-muted">
                                {{ $t('academy.allowed_formats') }}
                            </small>
                        </b-form-group>

                        <!-- Vista previa de la nueva imagen -->
                        <div v-if="newImagePreview" class="new-image-preview mt-3">
                            <h6>{{ $t('academy.preview') }}:</h6>
                            <div class="image-preview-container">
                                <img 
                                    :src="newImagePreview" 
                                    :alt="$t('academy.new_image')"
                                    class="new-preview-image"
                                />
                            </div>
                        </div>

                        <!-- Loading -->
                        <div v-if="isUploadingImage" class="text-center mt-3">
                            <b-spinner small></b-spinner>
                            <span class="ml-2">{{ $t('academy.uploading_image') }}</span>
                        </div>
                    </div>
                </b-modal>
            </div>
        </main>
        <div v-else class="text-center py-5">
            <b-spinner></b-spinner>
            <p>{{ $t('academy.loading_institution_data') }}...</p>
        </div>
    </AcademyLayout>
</template>

<script>
import AcademyLayout from '@/layouts/AcademyLayout.vue';
import CoursesDataTest from '@/views/TestEducationalInstitutions/coursesDataTest'
import { mapGetters } from 'vuex';
import educationalInstitutionsData from '@/data/educationalInstitutionsData';

import { 
    BIconPlusCircle, 
    BIconPencil, 
    BIconTrash, 
    BIconCollection,
    BIconPlus
} from 'bootstrap-vue';
import Swal from 'sweetalert2';
import noImage from '@/assets/svg/no-image.svg';
import { storage } from '@/data/firebaseConfig';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';


export default {
    name: 'CourseManagementView',
    components: {
        AcademyLayout,
        BIconPlusCircle,
        BIconPencil,
        BIconTrash,
        BIconCollection,
        BIconPlus
    },
    data() {
        return {
            showImageEditorModal: false,
            currentImageCourse: null,
            newImageFile: null,
            newImagePreview: null,
            isUploadingImage: false,
            noImage,
            courses: [],
            filteredCourses: [],
            searchQuery: '',
            selectedStatus: null,
            selectedModality: null,
            showCreateModal: false,
            showModulesModal: false,
            isEditing: false,
            currentCourse: null,
            selectedFile: null,
            institutionData: null,
            courseForm: {
                name: '',
                description: '',
                image: '',
                isRemote: true,
                block: false,
                institution: '',
                modules: [],
                modality: 'free'
            },
            statusOptions: [
                { value: null, text: 'Todos los estados' },
                { value: false, text: 'Activos' },
                { value: true, text: 'Bloqueados' }
            ],
            modalityOptions: [
                { value: null, text: 'Todas las modalidades' },
                { value: true, text: 'Remoto' },
                { value: false, text: 'Presencial' }
            ],
            selectedCourseBanner: null
        };
    },
    computed: {
        ...mapGetters('auth', ['user']),
        fields() {
            return [
                { key: 'image', label: this.$t('academy.cover'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'name', label: this.$t('academy.name') },
                { key: 'institution', label: this.$t('academy.institution'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'modules', label: this.$t('academy.lessons'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'modality', label: this.$t('academy.modality'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'status', label: this.$t('academy.status'), thClass: 'text-center', tdClass: 'text-center' },
                { key: 'actions', label: this.$t('academy.actions'), thClass: 'text-center', tdClass: 'text-center' }
            ]
        }
    },
    async mounted() {
        await this.loadInstitutionData();
        await this.loadCourses();
    },
    methods: {

        openImageEditor(course) {
        console.log('Abriendo editor de imagen para:', course.name);
        this.currentImageCourse = { ...course };
        this.showImageEditorModal = true;
    },

    handleNewImageChange(event) {
        const file = event.target.files[0];
        if (!file) {
            this.newImagePreview = null;
            return;
        }

        // Validaciones simples
        if (file.size > 5 * 1024 * 1024) { // 5MB
            Swal.fire({
                icon: 'error',
                title: this.$t('academy.file-too-large'),
                text: this.$t('academy.file_size_error')
            });
            this.newImageFile = null;
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            Swal.fire({
                icon: 'error',
                title: this.$t('academy.invalid-format'),
                text: this.$t('academy.image_type_error')
            });
            this.newImageFile = null;
            return;
        }

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
            this.newImagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
        this.newImageFile = file;
    },

    async saveImageChanges(bvModalEvt) {
        bvModalEvt.preventDefault();

        if (!this.newImageFile) {
            Swal.fire({
                icon: 'warning',
                title: this.$t('academy.no_changes'),
                text: this.$t('academy.select_new_to_continue')
            });
            return;
        }

        try {
            this.isUploadingImage = true;

            const timestamp = Date.now();
            const fileExtension = this.newImageFile.name.split('.').pop().toLowerCase();
            const fileName = `courses/${this.currentImageCourse.id}/images/course-image-${timestamp}.${fileExtension}`;
            
            // Crear referencia y subir archivo
            const storageRef = ref(storage, fileName);
            const snapshot = await uploadBytes(storageRef, this.newImageFile);
            
            // Obtener URL de descarga
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            await CoursesDataTest.updateCourseImage(this.currentImageCourse.id, {
                url: downloadURL,
                path: fileName
            });

            // Eliminar imagen anterior si existe
            if (this.currentImageCourse.imagePath) {
                try {
                    const oldImageRef = ref(storage, this.currentImageCourse.imagePath);
                    await deleteObject(oldImageRef);
                } catch (error) {
                    console.warn('No se pudo eliminar imagen anterior:', error);
                }
            }

            // Recargar cursos
            await this.loadCourses();

            // Cerrar modal
            this.showImageEditorModal = false;

            // Mostrar éxito
            Swal.fire({
                icon: 'success',
                title: '¡Éxito!',
                text: this.$t('academy.update_image_success'),
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error al actualizar imagen:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: this.$t('academy.update_image_error')
            });
        } finally {
            this.isUploadingImage = false;
        }
    },

    resetImageEditor() {
        this.currentImageCourse = null;
        this.newImageFile = null;
        this.newImagePreview = null;
        this.isUploadingImage = false;
    },

        handleBannerUpdate(updatedBannerData) {
            this.selectedCourseBanner = updatedBannerData;
            this.loadCourses();
            },

            // Método para configurar el banner cuando se selecciona un curso
            setCourseForBannerEdit(course) {
            this.selectedCourseBanner = {
                courseId: course.id,
                courseName: course.name,
                courseDescription: course.description,
                institution: course.institution,
                modality: course.modality,
                modulesCount: course.modules?.length || 0,
                bannerImage: course.imageBanner || course.image,
                backgroundColor: course.backgroundColor || '#667eea',
                institutionLogo: course.institutionLogo
            };
        },
        async loadInstitutionData() {
            if (!this.user?.uid) return;
            try {
                const institution = await educationalInstitutionsData.getInstitutionByAdminUid(this.user.uid);
                this.institutionData = institution;
                // Si no hay institución, redirigir o mostrar error
                if (!institution) {
                    this.$router.push('/');
                    return;
                }
            } catch (error) {
                console.error('Error al cargar datos de la institución:', error);
                this.$router.push('/');
            }
        },
        async loadCourses() {
            try {
                if (!this.institutionData) return;
                
                // Obtener todos los cursos y filtrar por institución
                const allCourses = await CoursesDataTest.getAllCourses();
                this.courses = allCourses.filter(course => 
                    course.institution === this.institutionData.name
                );
                this.filteredCourses = [...this.courses];
            } catch (error) {
                console.error('Error al cargar cursos:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.load-failed-courses')
                });
            }
        },
        filterCourses() {
            this.filteredCourses = this.courses.filter(course => {
                const matchesSearch = course.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                                    course.description.toLowerCase().includes(this.searchQuery.toLowerCase());
                const matchesStatus = this.selectedStatus === null || course.block === this.selectedStatus;
                const matchesModality = this.selectedModality === null || course.isRemote === this.selectedModality;
                return matchesSearch && matchesStatus && matchesModality;
            });
        },
        navigateEditCourse(course) {
            this.$router.push({ 
                name: 'student-backoffice-test',
                params: { 
                    courseId: course.id // Usamos el ID del curso directamente
                }
            });
        },
        async handleSaveCourse() {
            try {
                const courseData = {
                    name: this.courseForm.name,
                    description: this.courseForm.description,
                    image: this.courseForm.image,
                    isRemote: this.courseForm.isRemote,
                    block: this.courseForm.block,
                    institution: this.institutionData.name, // Asignar la institución actual
                    modules: this.courseForm.modules,
                    modality: this.courseForm.modality,
                    bannerImage: this.courseForm.bannerImage,
                    isPublished: false 
                };

                if (this.isEditing) {
                    await CoursesDataTest.updateCourse(this.currentCourse.id, courseData);
                } else {
                    await CoursesDataTest.createCourse(courseData);
                }

                await this.loadCourses();
                this.showCreateModal = false;
                Swal.fire({
                    icon: 'success',
                    title: this.$t('academy.success'),
                    text: this.isEditing ? this.$t('academy.course-updated') : this.$t('academy.course-created')
                });
            } catch (error) {
                console.error('Error al guardar curso:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('academy.course-save-failed')
                });
            }
        },
        async confirmDelete(course) {
            const result = await Swal.fire({
                title: this.$t('academy.are-you-sure'),
                text: this.$t('academy.delete_course_warning'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: this.$t('academy.confirm_delete'),
                cancelButtonText: this.$t('academy.cancel')
            });

            if (result.isConfirmed) {
                try {
                    await CoursesDataTest.deleteCourse(course.id);
                    await this.loadCourses();
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('academy.deleted'),
                        text: this.$t('academy.course-deleted')
                    });
                } catch (error) {
                    console.error('Error al eliminar curso:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.course-delete-failed')
                    });
                }
            }
        },
        manageModules(course) {
            this.currentCourse = course;
            this.showModulesModal = true;
        },
        addModule() {
            const moduleNumber = (this.courseForm.modules.length + 1).toString();
            const newModule = {
                name: '',
                description: '',
                number: moduleNumber,
                image: '',
                lessons: [{
                    classActivities: []
                }]
            };
            this.courseForm.modules.push(newModule);
        },
        removeModule(moduleIndex) {
            this.courseForm.modules.splice(moduleIndex, 1);
            // Renumerar Lecciones
            this.courseForm.modules.forEach((module, index) => {
                module.number = (index + 1).toString();
            });
        },
        addActivity(moduleIndex) {
            if (!this.courseForm.modules[moduleIndex].lessons[0].classActivities) {
                this.courseForm.modules[moduleIndex].lessons[0].classActivities = [];
            }
            
            const activityId = `activity${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
            const newActivity = {
                id: activityId,
                idStart: `${activityId}Start`,
                name: '',
                InitialProjectDescription: '',
                introductionText: '',
                code: "#CODIGO DEL PROYECTO",
                componentsDiagram: this.$t('academy.activity-link'),
                stepByStepGuide: this.$t('academy.pending')
            };
            
            this.courseForm.modules[moduleIndex].lessons[0].classActivities.push(newActivity);
        },
        removeActivity(moduleIndex, activityIndex) {
            this.courseForm.modules[moduleIndex].lessons[0].classActivities.splice(activityIndex, 1);
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validaciones básicas
            if (file.size > 2 * 1024 * 1024) { // 2MB
                Swal.fire({
                    icon: 'error',
                    title: this.$t('academy.file-too-large'),
                    text: this.$t('academy.file-size-limit')
                });
                return;
            }

            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire({
                    icon: 'error',
                    title: this.$t('academy.invalid-format'),
                    text: this.$t('academy.allowed-formats')
                });
                return;
            }

            // Aquí deberías implementar la subida a Firebase Storage
            // Por ahora, creamos una URL temporal para preview
            const reader = new FileReader();
            reader.onload = (e) => {
                this.courseForm.image = e.target.result;
            };
            reader.readAsDataURL(file);

            // TODO: Implementar subida real a Firebase Storage
            // const imageUrl = await uploadImageToFirebase(file);
            // this.courseForm.image = imageUrl;
        },
        handleImageError(event, course) {
            console.warn(`Error cargando imagen para curso: ${course.name}`);
            event.target.src = this.noImage;
        },
        truncateText(text, length) {
            if (!text) return '';
            return text.length > length ? text.substring(0, length) + '...' : text;
        },
        async confirmDeleteModule(moduleIndex) {
            const result = await Swal.fire({
                title: this.$t('academy.are-you-sure'),
                text: 'Esta acción eliminará el Lección y todas sus actividades',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: this.$t('academy.confirm_delete'),
                cancelButtonText: this.$t('academy.cancel')
            });

            if (result.isConfirmed) {
                try {
                    await CoursesDataTest.removeModuleFromCourse(this.currentCourse.id, moduleIndex);
                    await this.loadCourses();
                    // Actualizar el curso actual
                    this.currentCourse = this.courses.find(c => c.id === this.currentCourse.id);
                    Swal.fire({
                        icon: 'success',
                        title: this.$t('academy.deleted'),
                        text: 'La Lección ha sido eliminado correctamente'
                    });
                } catch (error) {
                    console.error('Error al eliminar Lección:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: this.$t('academy.delete_error')
                    });
                }
            }
        },
        editModule(moduleIndex) {
            // Cerrar modal de Lecciones y abrir modal de edición con el lección seleccionado
            this.showModulesModal = false;
            this.navigateEditCourse(this.currentCourse);
        },
        resetForm() {
            this.isEditing = false;
            this.currentCourse = null;
            this.selectedFile = null;
            this.courseForm = {
                name: '',
                description: '',
                image: '',
                isRemote: true,
                block: false,
                institution: '',
                modules: [],
                modality: 'free'
            };
        }
    }
};
</script>

<style lang="scss" scoped>

.course-image-container {
    position: relative;
    display: inline-block;
    overflow: hidden;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

        .image-edit-overlay {
            opacity: 1;
            visibility: visible;
        }
    }

    .course-image {
        width: 80px;
        height: 60px;
        object-fit: cover;
        border-radius: 8px;
        border: 2px solid #dee2e6;
        transition: all 0.3s ease;
        cursor: pointer;
    }

    .image-edit-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
        border-radius: 8px;

        .edit-image-btn {
            background: rgba(255, 255, 255, 0.95);
            border: none;
            border-radius: 50%;
            width: 35px;
            height: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            transition: all 0.2s ease;

            &:hover {
                background: white;
                transform: scale(1.1);
            }

            .b-icon {
                font-size: 16px;
                color: #495057;
            }
        }
    }
}

/* Estilos para el modal */
.course-info-header {
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.image-preview-container {
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 15px;
    min-height: 120px;
}

.current-preview-image,
.new-preview-image {
    max-width: 100%;
    max-height: 150px;
    object-fit: cover;
    border-radius: 8px;
    border: 2px solid #dee2e6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.new-image-preview {
    border: 2px solid #28a745;
    border-radius: 8px;
    padding: 15px;
    background: #f8fff9;

    h6 {
        color: #28a745;
        font-weight: 600;
    }
}

.modality-options {
  gap: 1rem;
}

.modality-btn {
  border-radius: 1.5rem;
  padding: 0.6rem 1.4rem;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.25s ease;
  border: none;
}

.modality-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.btn-modality-active {
  background-color: #e0f7fa;
  color: #007bff;
}

.btn-modality-active-premium {
  background-color: #fff3cd;
  color: #ff8c00;
}

.btn-modality-inactive {
  background-color: #f8f9fa;
  color: #6c757d;
}

.btn-modality-inactive:hover {
  background-color: #e2e6ea;
  color: #495057;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity .3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.courses-management-container {
    background: white;
    border-radius: 15px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin-top: 20px;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
        margin: 0;
        color: #333;
    }
}

.filters-section {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.courses-table {
    margin-top: 20px;

    ::v-deep th {
        background-color: #f8f9fa;
        font-weight: 600;
    }
}

.course-image {
    img {
        width: 60px;
        height: 40px;
        object-fit: cover;
        border-radius: 5px;
        border: 1px solid #dee2e6;
    }
}

.action-buttons {
    display: flex;
    gap: 5px;
    justify-content: center;
}

.image-upload-section {
    .current-image {
        margin-bottom: 10px;
        
        img {
            width: 100%;
            max-height: 200px;
            object-fit: cover;
            border-radius: 8px;
            border: 1px solid #dee2e6;
        }
    }
}

.modules-configuration {
    .add-module-section {
        text-align: center;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 5px;
    }
}

.module-item {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
    background: #f8f9fa;

    .module-header {
        margin-bottom: 10px;
    }

    .activities-section {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #dee2e6;

        .activity-item {
            margin-bottom: 10px;
            padding: 8px;
            background: white;
            border-radius: 5px;
            border: 1px solid #e9ecef;
        }
    }
}

.modules-management {
    .course-info {
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
    }
}

.module-detail-card {
    margin-bottom: 15px;

    .module-detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        h6 {
            margin: 0;
            color: #333;
        }

        .module-actions {
            display: flex;
            gap: 5px;
        }
    }

    .activities-summary {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid #e9ecef;

        ul {
            margin: 10px 0;
            padding-left: 20px;
        }
    }
}

::v-deep .modal-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

::v-deep .modal-footer {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
}

// Responsive adjustments
@media (max-width: 768px) {
    .action-buttons {
        flex-direction: column;
        gap: 3px;
    }
    
    .course-image img {
        width: 40px;
        height: 30px;
    }
}
</style>